// JSON-LD schema generators for SEO. All return plain JS objects ready to JSON.stringify.
import { LOCATIONS, PHONE_DISPLAY, SOCIAL } from "../data/site";

const ORIGIN = "https://drindreshdixit.com"; // canonical site origin used in URLs

// ---------- Doctor / Physician ----------
export const physicianSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Physician",
  "@id": `${ORIGIN}/#physician`,
  name: "Dr. Indresh Dixit",
  honorificPrefix: "Dr.",
  jobTitle: "Gastroenterologist & Liver Specialist",
  medicalSpecialty: ["Gastroenterology", "Hepatology"],
  description:
    "DM Gastroenterology (BHU), 10+ years experience. Advanced endoscopy, hepatology and therapeutic GI practice in Varanasi.",
  image:
    "https://customer-assets.emergentagent.com/job_varanasi-gastro/artifacts/jkchhm21_images%20%282%29.jpeg",
  url: ORIGIN,
  telephone: PHONE_DISPLAY,
  sameAs: [SOCIAL.youtube, SOCIAL.facebook, SOCIAL.instagram],
  alumniOf: {
    "@type": "CollegeOrUniversity",
    name: "Banaras Hindu University (BHU), Institute of Medical Sciences",
  },
  memberOf: [
    { "@type": "Organization", name: "Indian Society of Gastroenterology (ISG)" },
    {
      "@type": "Organization",
      name: "Indian National Association for Study of the Liver (INASL)",
    },
    {
      "@type": "Organization",
      name: "Society of GI Endoscopy of India (SGEI)",
    },
  ],
  worksFor: LOCATIONS.map((l) => ({
    "@type": "MedicalClinic",
    name: l.name,
    address: l.address,
  })),
});

// ---------- Medical Business / Local Business per clinic ----------
export const medicalClinicSchemas = () =>
  LOCATIONS.map((l) => ({
    "@context": "https://schema.org",
    "@type": ["MedicalBusiness", "LocalBusiness", "MedicalClinic"],
    "@id": `${ORIGIN}/#clinic-${l.id}`,
    name: l.name,
    description:
      "Gastroenterology, Liver and Endoscopy clinic of Dr. Indresh Dixit in Varanasi.",
    image:
      "https://customer-assets.emergentagent.com/job_varanasi-gastro/artifacts/jkchhm21_images%20%282%29.jpeg",
    address: {
      "@type": "PostalAddress",
      streetAddress: l.address,
      addressLocality: "Varanasi",
      addressRegion: "Uttar Pradesh",
      postalCode: l.id === "lanka" ? "221005" : "221001",
      addressCountry: "IN",
    },
    telephone: PHONE_DISPLAY,
    url: ORIGIN,
    sameAs: [SOCIAL.youtube, SOCIAL.facebook, SOCIAL.instagram],
    priceRange: "₹₹",
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      opens: l.id === "lanka" ? "08:30" : "18:00",
      closes: l.id === "lanka" ? "17:00" : "21:00",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "900",
    },
    medicalSpecialty: ["Gastroenterology", "Hepatology"],
    availableService: [
      { "@type": "MedicalProcedure", name: "Upper GI Endoscopy" },
      { "@type": "MedicalProcedure", name: "Colonoscopy" },
      { "@type": "MedicalProcedure", name: "ERCP" },
      { "@type": "MedicalProcedure", name: "Endoscopic Ultrasound (EUS)" },
      { "@type": "MedicalProcedure", name: "FibroScan" },
    ],
  }));

// ---------- FAQPage ----------
export const faqPageSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

// ---------- Breadcrumb ----------
export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.label,
    item: it.url ? `${ORIGIN}${it.url}` : undefined,
  })),
});

// ---------- MedicalCondition (for disease pages) ----------
export const medicalConditionSchema = ({ name, alternateName, signOrSymptom, possibleTreatment, riskFactor }) => ({
  "@context": "https://schema.org",
  "@type": "MedicalCondition",
  name,
  alternateName,
  signOrSymptom: (signOrSymptom || []).map((s) => ({
    "@type": "MedicalSignOrSymptom",
    name: s,
  })),
  possibleTreatment: (possibleTreatment || []).map((t) => ({
    "@type": "MedicalTherapy",
    name: t,
  })),
  riskFactor: (riskFactor || []).map((r) => ({
    "@type": "MedicalRiskFactor",
    name: r,
  })),
});

// ---------- MedicalProcedure (for procedure pages) ----------
export const medicalProcedureSchema = ({
  name,
  description,
  preparation,
  followup,
  howPerformed,
  indication,
}) => ({
  "@context": "https://schema.org",
  "@type": "MedicalProcedure",
  name,
  description,
  preparation,
  followup,
  howPerformed,
  indication: (indication || []).map((i) => ({
    "@type": "MedicalIndication",
    name: i,
  })),
});

// ---------- WebPage / Website ----------
export const webPageSchema = ({ name, description, url }) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name,
  description,
  url: `${ORIGIN}${url}`,
  isPartOf: { "@type": "WebSite", name: "Dr. Indresh Dixit", url: ORIGIN },
});
